import { useState, useEffect } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { DayPicker } from 'react-day-picker';
import { es } from 'date-fns/locale';
import { Calendar } from 'lucide-react';
import { toColombiaDateString } from '../../utils/date';
import 'react-day-picker/style.css';

export interface DatePickerInputProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
}

export function DatePickerInput({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  className = '',
}: DatePickerInputProps) {
  const [selected, setSelected] = useState<Date | undefined>(
    value ? new Date(value + 'T12:00:00') : undefined
  );

  // Sincronizar cuando el valor cambia desde fuera
  useEffect(() => {
    setSelected(value ? new Date(value + 'T12:00:00') : undefined);
  }, [value]);

  const displayValue = selected
    ? selected.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  const handleSelect = (date: Date | undefined) => {
    setSelected(date);
    if (date) {
      onChange(toColombiaDateString(date));
    } else {
      onChange('');
    }
  };

  return (
    <Popover className={`relative ${className}`}>
      <PopoverButton className="relative flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 shadow-sm hover:border-emerald-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all w-[220px] shrink-0 text-left">
        <Calendar className="w-4 h-4 text-emerald-500 shrink-0" />
        <span className={`text-sm select-none ${value ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
          {displayValue || placeholder}
        </span>
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom start"
        className="z-50 mt-1 rounded-xl bg-white p-3 shadow-xl ring-1 ring-black/5 transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          showOutsideDays
          locale={es}
          captionLayout="dropdown-years"
          startMonth={new Date(2020, 0)}
          endMonth={new Date(2030, 11)}
          classNames={{
            root: 'rdp',
            months: 'flex flex-col',
            month: 'space-y-3',
            month_caption: 'flex justify-center pt-1 relative items-center',
            caption_label: 'text-sm font-medium text-gray-900',
            nav: 'space-x-1 flex items-center',
            button_previous:
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border border-gray-200 rounded-md flex items-center justify-center absolute left-1',
            button_next:
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border border-gray-200 rounded-md flex items-center justify-center absolute right-1',
            month_grid: 'w-full border-collapse space-y-1',
            weekdays: 'flex',
            weekday: 'text-gray-500 rounded-md w-9 font-normal text-[0.8rem]',
            week: 'flex w-full mt-2',
            day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-emerald-100 hover:text-emerald-900 text-center text-sm cursor-pointer',
            day_button: 'w-full h-full flex items-center justify-center rounded-md cursor-pointer',
            selected:
              'bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white focus:bg-emerald-600 focus:text-white',
            today: 'bg-gray-100 text-gray-900',
            outside: 'text-gray-400 opacity-50',
            disabled: 'text-gray-400 opacity-50',
            hidden: 'invisible',
          }}
        />
      </PopoverPanel>
    </Popover>
  );
}
